import React from 'react';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Avatar,
  Button,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Analytics as AnalyticsIcon,
  Assignment as AssignmentIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';

const BareDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Mock data for Bare Biologics
  const mockInventory = [
    { id: 1, productName: 'Lipoderma Base Material', batchId: 'LT-2024-001', quantityAvailable: 150, status: 'Available' },
    { id: 2, productName: 'Processing Supplies', batchId: 'PS-2024-001', quantityAvailable: 75, status: 'Available' },
    { id: 3, productName: 'Quality Control Kits', batchId: 'QC-2024-001', quantityAvailable: 25, status: 'Low Stock' }
  ];

  const mockPurchaseOrders = [
    { id: 'PO-001', vendor: 'Britecyte', productName: 'Lipoderma Base Material', quantity: 100, status: 'Pending', priority: 'High' },
    { id: 'PO-002', vendor: 'Lab Supply Co', productName: 'Processing Supplies', quantity: 50, status: 'Processing', priority: 'Medium' }
  ];

  const mockRecentActivity = [
    { id: 1, action: 'Inventory Received', item: 'Lipoderma Base Material', time: '2 hours ago', status: 'Completed' },
    { id: 2, action: 'Purchase Order Created', item: 'PO-003 for Britecyte', time: '4 hours ago', status: 'Pending' },
    { id: 3, action: 'Quality Check Completed', item: 'Batch LT-2024-001', time: '6 hours ago', status: 'Approved' }
  ];

  // Calculate statistics
  const totalInventory = mockInventory.reduce((sum, item) => sum + item.quantityAvailable, 0);
  const pendingPOs = mockPurchaseOrders.filter(po => po.status === 'Pending').length;
  const lowStockItems = mockInventory.filter(item => item.quantityAvailable < 30).length;
  const qualityChecksPending = 2;

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <Card sx={{ 
      height: '100%', 
      border: '1px solid #e2e8f0',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        transform: 'translateY(-2px)',
        transition: 'all 0.3s ease'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ color, fontWeight: 700, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                  {trend}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: 64,
              height: 64,
              background: `linear-gradient(135deg, ${color}20, ${color}10)`,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${color}30`
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Breadcrumbs />
      
      {/* Bare Biologics Header */}
      <Box sx={{ 
        mb: 4, 
        p: 4, 
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)', 
        borderRadius: 2,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            zIndex: 0
          }}
        />
        
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ 
            mr: 3, 
            bgcolor: 'rgba(255, 255, 255, 0.15)', 
            width: 64, 
            height: 64,
            border: '3px solid rgba(255, 255, 255, 0.2)'
          }}>
            <ScienceIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 0.5, color: 'white', letterSpacing: '-0.02em' }}>
              Bare Biologics Operations
            </Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 500, opacity: 0.9 }}>
              Lipoderma Production & Supply Chain Management
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mt: 1,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Chip 
                label="Primary Vendor: Britecyte" 
                size="small" 
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  width: { xs: 'fit-content', sm: 'auto' }
                }} 
              />
              <Chip 
                label="QuickBooks Integration" 
                size="small" 
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  width: { xs: 'fit-content', sm: 'auto' }
                }} 
              />
            </Box>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ 
          color: 'rgba(255, 255, 255, 0.9)', 
          position: 'relative', 
          zIndex: 1,
          fontSize: '1.1rem',
          lineHeight: 1.6,
          maxWidth: 700,
          fontWeight: 500
        }}>
          Specialized in regenerative medicine and tissue engineering solutions. Manage Lipoderma production, 
          purchase orders from Britecyte, and maintain quality control standards.
        </Typography>
      </Box>

      {/* Operational Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Inventory"
            value={totalInventory.toLocaleString()}
            subtitle="Lipoderma materials & supplies"
            icon={<InventoryIcon />}
            color="#039582"
            trend="+15% vs last month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending POs"
            value={pendingPOs}
            subtitle="awaiting fulfillment"
            icon={<OrdersIcon />}
            color="#039582"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Low Stock Alerts"
            value={lowStockItems}
            subtitle="require attention"
            icon={<WarningIcon />}
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Quality Checks"
            value={qualityChecksPending}
            subtitle="pending approval"
            icon={<CheckCircleIcon />}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Purchase Orders */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AssignmentIcon sx={{ mr: 1, color: '#5c725c' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      Recent Purchase Orders
                    </Typography>
                  </Box>
                  <IconButton 
                    size="small"
                    sx={{ 
                      color: '#6b7280',
                      '&:hover': { bgcolor: '#f0fdfa', color: '#0d9488' }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  Britecyte orders and supply chain management
                </Typography>
              </Box>
              {isMobile ? (
                <Stack spacing={1.5} sx={{ p: 2 }}>
                  {mockPurchaseOrders.map((po, index) => (
                    <Paper key={po.id} variant="outlined" sx={{ p: 2, borderLeft: index === 0 ? '3px solid #0d9488' : '3px solid transparent' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{po.id}</Typography>
                        <Chip label={po.status} color={po.status === 'Processing' ? 'success' : 'warning'} size="small" sx={{ fontWeight: 600 }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">{po.vendor}</Typography>
                        <Typography variant="body2" color="text.secondary">Qty: {po.quantity}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flexBasis: '100%' }}>{po.productName}</Typography>
                      </Box>
                      <Chip
                        label={po.priority}
                        size="small"
                        sx={{
                          bgcolor: po.priority === 'High' ? '#fef2f2' : '#fef3c7',
                          color: po.priority === 'High' ? '#dc2626' : '#d97706',
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafbfc' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>PO ID</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Priority</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mockPurchaseOrders.map((po, index) => (
                        <TableRow 
                          key={po.id} 
                          hover 
                          sx={{ 
                            '&:hover': { bgcolor: '#f6f7f6' },
                            borderLeft: index === 0 ? '3px solid #0d9488' : '3px solid transparent'
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#0f172a' }}>
                            {po.id}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500, color: '#374151' }}>{po.vendor}</TableCell>
                          <TableCell sx={{ color: '#374151' }}>{po.productName}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{po.quantity}</TableCell>
                          <TableCell>
                            <Chip
                              label={po.status}
                              color={po.status === 'Processing' ? 'success' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={po.priority}
                              size="small"
                              sx={{
                                bgcolor: po.priority === 'High' ? '#fef2f2' : '#fef3c7',
                                color: po.priority === 'High' ? '#dc2626' : '#d97706',
                                fontWeight: 600,
                                fontSize: '0.7rem'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AnalyticsIcon sx={{ color: '#5c725c', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                    Recent Activity
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Latest operations and updates
                </Typography>
              </Box>
              
              <Box sx={{ p: 3 }}>
                {mockRecentActivity.map((activity) => (
                  <Paper
                    key={activity.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          bgcolor: activity.status === 'Completed' ? '#10b981' : '#f59e0b',
                          borderRadius: '50%',
                          mt: 0.5
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                          {activity.action}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#374151', mb: 0.5 }}>
                          {activity.item}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {activity.time}
                        </Typography>
                      </Box>
                      <Chip 
                        label={activity.status} 
                        size="small"
                        color={activity.status === 'Completed' ? 'success' : 'warning'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', mb: 3 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AssignmentIcon />}
              sx={{ 
                py: 2, 
                borderColor: '#5c725c', 
                color: '#5c725c',
                '&:hover': { borderColor: '#4a5d4a', bgcolor: '#f0f4f0' }
              }}
            >
              Create PO
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<InventoryIcon />}
              sx={{ 
                py: 2, 
                borderColor: '#0d9488', 
                color: '#0d9488',
                '&:hover': { borderColor: '#0b7a6e', bgcolor: '#f0fdfa' }
              }}
            >
              Receive Inventory
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              sx={{ 
                py: 2, 
                borderColor: '#f59e0b', 
                color: '#f59e0b',
                '&:hover': { borderColor: '#d97706', bgcolor: '#fef3c7' }
              }}
            >
              Quality Check
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ShippingIcon />}
              sx={{ 
                py: 2, 
                borderColor: '#ef4444', 
                color: '#ef4444',
                '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' }
              }}
            >
              Ship to QB
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BareDashboard;
